import React from 'react';
import s from './Price.scss';
import PromoMessage from './PromoMessage/PromoMessage';
import { cn } from '@express-labs/raven-tools';

type CommonPriceProps = {
  className?: string;
  clearancePromoMessage?: string;
  layout?: 'stacked' | 'condensed';
  listPrice: string;
  promoMessage?: string | PromoMessageArrayProps[];
  promoMessageContainerClassName?: string;
  salePrice?: string;
};

type IsEnsemble = {
  isEnsemble: true;
  productId: string;
};

type IsNotEnsemble = {
  isEnsemble?: false;
  productId?: string;
};

export type PriceProps = CommonPriceProps & (IsEnsemble | IsNotEnsemble);

/**
 * Price handles the display of product prices, sales prices, and promotional messages.

## Examples
```html
<div className={s.demoItem} >
  <p>ListPrice</p>
  <Price listPrice="$45.99" />
</div>

<div className={s.demoItem} >
  <p>SalePrice, No message (stacked by default)</p>
  <Price listPrice="$45.99" salePrice="$32.32" />
</div>

<div className={s.demoItem} >
  <p>Promo Message and Sale Price (stacked by default)</p>
  <Price listPrice="$45.99" salePrice="$32.32" promoMessage="This product is cheaper" />
</div>

<div className={s.demoItem} >
  <p>Promo Message, Sale Price, and Condensed</p>
  <Price listPrice="$45.99" salePrice="$32.32" promoMessage="This product is cheaper" layout={'condensed'} />
</div>
```
 */
class Price extends React.Component<PriceProps> {
  public static displayName = 'Price';
  getListPrice(onSale: boolean) {
    const { listPrice } = this.props;

    const listPriceStyle = onSale ? s.old : s.regular;

    return <span className={listPriceStyle}>{listPrice}</span>;
  }

  getOnSalePrice() {
    const { salePrice } = this.props;

    return <span className={s.sale}> {salePrice}</span>;
  }

  getLayoutClass() {
    const { layout } = this.props;

    switch (layout) {
      case 'stacked':
        return s.layoutStacked;
      case 'condensed':
        return s.layoutCondensed;
      default:
        return s.layoutStacked;
    }
  }

  render() {
    const {
      className,
      clearancePromoMessage,
      isEnsemble,
      listPrice,
      productId,
      promoMessage,
      promoMessageContainerClassName,
      salePrice,
    } = this.props;

    const onSale = salePrice !== '$0.00' && salePrice !== listPrice;

    const list = this.getListPrice(onSale);
    const sale = onSale ? this.getOnSalePrice() : null;

    let ariaLabel = '';
    if (!onSale) {
      ariaLabel = listPrice;
    } else {
      ariaLabel = `${salePrice} marked down from ${listPrice}`;
    }

    return (
      <div className={cn(this.getLayoutClass(), 'price')}>
        <div className="srOnly">{ariaLabel}</div>
        <p aria-hidden className={cn(className, s.price, 'price__price')}>
          {list}
          {sale}
        </p>
        {isEnsemble && (
          <PromoMessage
            className={cn('price__message', promoMessageContainerClassName)}
            clearancePromoMessage={clearancePromoMessage}
            isEnsemble
            productId={productId}
            onSale={onSale}
            promoMessage={promoMessage}
          />
        )}
        {!isEnsemble && (
          <PromoMessage
            className={cn('price__message', promoMessageContainerClassName)}
            clearancePromoMessage={clearancePromoMessage}
            onSale={onSale}
            promoMessage={promoMessage}
          />
        )}
      </div>
    );
  }
}

export default Price;
